import React, { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useToast } from 'app/hooks/useToast';
import { prependHttp } from 'utils/url';
import { Button, Field, Icon, IconButton, TextInput } from '@kontentino/ui';
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { BrandHubApi } from 'app/modules/brandHub/api/brandHub';
import { WebCrawlResult } from 'app/modules/brandHub/types';
import { ApiClientError } from 'api/client';
import { faImage } from '@fortawesome/pro-regular-svg-icons/faImage';
import KeyCodes from 'constants/keyCodes';
import useBoolean from 'utils/hooks/useBoolean';

type Props = {
  onChange(webCrawl: WebCrawlResult | undefined | null): void;
  value?: WebCrawlResult | null;
  dataNamePrefix: string;
  dataCy?: string;
  placeholder?: string;
  onUrlPaste?: (url: string) => void;
  autoFetch?: boolean;
};

type FormState = z.infer<typeof schema>;

const schema = z.object({
  url: z.string().url(),
});

const WebCrawlLinkField: FC<Props> = ({
  value,
  onChange,
  placeholder,
  dataNamePrefix,
  onUrlPaste,
  autoFetch = false,
}) => {
  const toast = useToast();
  const [imageErrored, setImageErrored] = useBoolean(false);
  const [isEditing, setIsEditing] = useBoolean(false);
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
    watch,
  } = useForm<FormState>({
    defaultValues: { url: value?.url ?? '' },
    resolver: zodResolver(schema),
  });

  const fetchWebCrawl = useMutation(BrandHubApi.fetchWebCrawl, {
    onSuccess(webCrawl) {
      onChange(webCrawl);
      setImageErrored.off();
      setIsEditing.off();
    },
    onError(e: ApiClientError) {
      toast(e?.userMessage ?? t('unableToRetrieveLinkInfo'), 'error');
    },
  });

  const registerUrl = register('url');
  const watchedUrl = watch('url');

  const onSubmit = handleSubmit(({ url }) =>
    fetchWebCrawl.mutate({ url: prependHttp(url) }),
  );

  function resetCrawl() {
    onChange(null);
    reset({ url: '' });
    fetchWebCrawl.reset();
    setIsEditing.off();
  }

  return (
    <Field.Group>
      {errors.url?.message && <Field.Error>{errors.url.message}</Field.Error>}
      <div
        className="tw-flex tw-gap-2"
        data-name={`${dataNamePrefix}-fetch-web-link`}
      >
        <div className="tw-flex-1">
          <TextInput
            disabled={Boolean(value) && !isEditing}
            {...registerUrl}
            className={value && !isEditing ? 'tw-pr-16' : 'tw-pr-8'}
            onPaste={(e) => {
              e.preventDefault();
              const pastedUrl = prependHttp(e.clipboardData.getData('text'));
              setValue('url', pastedUrl);

              if (onUrlPaste && pastedUrl) {
                onUrlPaste(pastedUrl);
              }

              if (autoFetch && pastedUrl) {
                fetchWebCrawl.mutate({ url: pastedUrl });
              }
            }}
            onBlur={(e) => {
              registerUrl.onBlur(e);
              if (e.target.value) {
                setValue('url', prependHttp(e.target.value));
              }
            }}
            onKeyDown={(e) => {
              if (e.key === KeyCodes.Enter) {
                e.preventDefault();
                setValue('url', prependHttp(e.currentTarget.value));
                onSubmit();
              }
            }}
            placeholder={placeholder ?? t('enterLinkToFetch')}
            iconAfter={
              value && (
                <div className="tw-flex tw-gap-2">
                  {!isEditing && (
                    <IconButton
                      variant="plain"
                      onClick={() => {
                        setIsEditing.on();
                        setValue('url', value.url);
                      }}
                    >
                      <Icon icon={faPen} />
                    </IconButton>
                  )}
                  <IconButton variant="plain" onClick={resetCrawl}>
                    <Icon icon={faTrash} />
                  </IconButton>
                </div>
              )
            }
          />
        </div>
        {(!value || isEditing) && (
          <Button
            type="button"
            className="tw-shrink-0"
            isLoading={fetchWebCrawl.isLoading}
            disabled={!watchedUrl}
            data-name={`${dataNamePrefix}-fetch-web-link_set-link`}
            onClick={onSubmit}
          >
            {t('fetchLink')}
          </Button>
        )}
      </div>
      {value && (
        <div className="tw-flex tw-items-center tw-gap-4 tw-rounded tw-border tw-border-grayscale-20 tw-bg-grayscale-5 tw-p-2">
          {value.image && !imageErrored ? (
            <img
              alt="url-preview"
              className="tw-h-20 tw-w-20 tw-shrink-0 tw-rounded tw-object-cover"
              src={value.image}
              onError={() => setImageErrored.on()}
            />
          ) : (
            <div className="tw-flex tw-h-20 tw-w-20 tw-shrink-0 tw-items-center tw-justify-center tw-rounded tw-bg-grayscale-20 tw-object-cover">
              <Icon icon={faImage} />
            </div>
          )}
          <div className="tw-min-w-0">
            <div className="tw-line-clamp-2 tw-text-base">{value.title}</div>
            <p className="tw-text-grasycale-120 tw-line-clamp-2 tw-text-sm">
              {value.description}
            </p>
          </div>
        </div>
      )}
    </Field.Group>
  );
};

export default WebCrawlLinkField;
